import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cartAPI, ordersAPI, authAPI, shippingAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import Layout from '@/components/Layout';
import { toast } from 'sonner';
import { CheckoutPayment } from '@/features/payments';
import { ShoppingBag, MapPin, User, Phone, Mail, Truck, Shield } from 'lucide-react';

const CheckoutPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [step, setStep] = useState('shipping'); // 'shipping', 'payment', 'success'
  const [shippingInfo, setShippingInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    notes: ''
  });
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState({});

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get user info
      const userData = await authAPI.getMe();
      setUser(userData);
      const savedAddress = userData.default_shipping_address || {};
      setShippingInfo(prev => ({
        ...prev,
        fullName: savedAddress.fullName || userData.name || '',
        email: userData.email || '',
        phone: savedAddress.phone || userData.phone || '',
        address: savedAddress.address || '',
        city: savedAddress.city || ''
      }));

      // Get cart
      const cart = await cartAPI.getCart();
      if (cart.length === 0) {
        toast.error('Your cart is empty');
        navigate('/cart');
        return;
      }
      setCartItems(cart);

      // Get shipping cities/zones - fetched live so the estimate shown
      // here can never drift out of sync with what the server actually
      // charges (the server is always the authority on the final amount).
      try {
        const shippingData = await shippingAPI.getCities();
        setCities(shippingData.cities || []);
        setZones(shippingData.zones || {});
      } catch (err) {
        console.error('Failed to load shipping cities:', err);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to checkout');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0);
  };

  const getShippingFee = () => {
    const subtotal = calculateSubtotal();
    const selectedCity = cities.find(c => c.city === shippingInfo.city);
    const zoneKey = selectedCity?.zone || 'other';
    const zone = zones[zoneKey];
    if (!zone) return 0; // zones not loaded yet - avoid showing a wrong estimate
    return subtotal >= zone.free_shipping_threshold ? 0 : zone.fee;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + getShippingFee();
  };

  const handleShippingSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!shippingInfo.fullName || !shippingInfo.phone || !shippingInfo.address || !shippingInfo.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Create order
      const orderData = {
        items: cartItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.product?.price || 0
        })),
        total: calculateTotal(),
        shipping_address: shippingInfo,
        status: 'pending_payment'
      };

      const order = await ordersAPI.create(orderData);
      setOrderId(order.order_id);
      setStep('payment');
      toast.success('Shipping info saved. Choose your payment method.');
    } catch (error) {
      console.error('Order creation error:', error);
      toast.error('Failed to create order. Please try again.');
    }
  };

  const handlePaymentSuccess = async (transaction) => {
    setStep('success');
    toast.success('Payment successful! Thank you for your order.');
    
    // Clear cart
    try {
      for (const item of cartItems) {
        await cartAPI.removeFromCart(item.product_id);
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const handlePaymentCancel = () => {
    toast.info('Payment cancelled');
  };

  if (loading) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-custom max-w-6xl">
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 skeleton h-96 rounded-2xl"></div>
              <div className="skeleton h-64 rounded-2xl"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (step === 'success') {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-custom max-w-2xl text-center">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
              <ShoppingBag className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Order Confirmed!</h1>
            <p className="text-lg text-muted-foreground mb-8">
              Thank you for your purchase. Your order #{orderId} has been confirmed.
            </p>
            <div className="bg-secondary/30 p-6 rounded-2xl mb-8">
              <p className="text-sm text-muted-foreground mb-2">Order Total</p>
              <p className="text-3xl font-bold font-mono">KES {calculateTotal().toLocaleString()}</p>
            </div>
            <p className="text-muted-foreground mb-8">
              We've sent a confirmation email to <strong>{shippingInfo.email}</strong>
            </p>
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                View Orders
              </Button>
              <Button className="btn-primary" onClick={() => navigate('/products')}>
                Continue Shopping
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="section-padding" data-testid="checkout-page">
        <div className="container-custom max-w-6xl">
          <h1 className="text-4xl font-bold mb-2">Checkout</h1>
          <p className="text-muted-foreground mb-8">Complete your order</p>

          {/* Progress Steps */}
          <div className="flex items-center mb-12">
            <div className={`flex items-center ${step === 'shipping' ? 'text-accent' : 'text-green-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'shipping' ? 'bg-accent text-white' : 'bg-green-600 text-white'}`}>
                1
              </div>
              <span className="ml-2 font-medium">Shipping</span>
            </div>
            <div className="flex-1 h-0.5 mx-4 bg-border">
              <div className={`h-full bg-green-600 transition-all ${step === 'payment' ? 'w-full' : 'w-0'}`}></div>
            </div>
            <div className={`flex items-center ${step === 'payment' ? 'text-accent' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'payment' ? 'bg-accent text-white' : 'bg-secondary'}`}>
                2
              </div>
              <span className="ml-2 font-medium">Payment</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {step === 'shipping' && (
                <Card className="luxury-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Shipping Information
                    </CardTitle>
                    <CardDescription>Where should we deliver your order?</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleShippingSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="fullName" className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Full Name *
                          </Label>
                          <Input
                            id="fullName"
                            value={shippingInfo.fullName}
                            onChange={(e) => setShippingInfo({ ...shippingInfo, fullName: e.target.value })}
                            placeholder="John Doe"
                            required
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            Email *
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            value={shippingInfo.email}
                            onChange={(e) => setShippingInfo({ ...shippingInfo, email: e.target.value })}
                            placeholder="john@example.com"
                            required
                            className="mt-2"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="phone" className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          Phone Number *
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={shippingInfo.phone}
                          onChange={(e) => setShippingInfo({ ...shippingInfo, phone: e.target.value })}
                          placeholder="+254 7XX XXX XXX"
                          required
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="address">Street Address *</Label>
                        <Input
                          id="address"
                          value={shippingInfo.address}
                          onChange={(e) => setShippingInfo({ ...shippingInfo, address: e.target.value })}
                          placeholder="123 Main Street, Apartment 4B"
                          required
                          className="mt-2"
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="city">City *</Label>
                          <Select
                            value={shippingInfo.city}
                            onValueChange={(v) => setShippingInfo({ ...shippingInfo, city: v })}
                          >
                            <SelectTrigger id="city" className="mt-2">
                              <SelectValue placeholder="Select your city" />
                            </SelectTrigger>
                            <SelectContent>
                              {cities.map((c) => (
                                <SelectItem key={c.city} value={c.city}>{c.city}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="postalCode">Postal Code</Label>
                          <Input
                            id="postalCode"
                            value={shippingInfo.postalCode}
                            onChange={(e) => setShippingInfo({ ...shippingInfo, postalCode: e.target.value })}
                            placeholder="00100"
                            className="mt-2"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="notes">Delivery Notes (Optional)</Label>
                        <Textarea
                          id="notes"
                          value={shippingInfo.notes}
                          onChange={(e) => setShippingInfo({ ...shippingInfo, notes: e.target.value })}
                          placeholder="Any special instructions for delivery..."
                          rows={3}
                          className="mt-2"
                        />
                      </div>

                      <Button type="submit" className="w-full btn-primary" size="lg">
                        Continue to Payment
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              {step === 'payment' && (
                <CheckoutPayment
                  orderId={orderId}
                  amount={calculateTotal()}
                  currency="KES"
                  customerEmail={shippingInfo.email}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentCancel={handlePaymentCancel}
                />
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div>
              <Card className="luxury-card sticky top-24">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5" />
                    Order Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Cart Items */}
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {cartItems.map((item) => (
                      <div key={item.cart_item_id} className="flex gap-3">
                        <img
                          src={item.product?.images?.[0] || 'https://via.placeholder.com/60'}
                          alt={item.product?.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.product?.name}</p>
                          <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                          <p className="text-sm font-mono">
                            KES {((item.product?.price || 0) * item.quantity).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Separator />

                  {/* Totals */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>KES {calculateSubtotal().toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Truck className="w-4 h-4" />
                        Shipping
                      </span>
                      <span>
                        {!shippingInfo.city
                          ? 'Select city'
                          : getShippingFee() === 0
                          ? 'FREE'
                          : `KES ${getShippingFee().toLocaleString()}`}
                      </span>
                    </div>
                    {shippingInfo.city && getShippingFee() > 0 && (() => {
                      const selectedCity = cities.find(c => c.city === shippingInfo.city);
                      const zone = zones[selectedCity?.zone];
                      if (!zone) return null;
                      const remaining = zone.free_shipping_threshold - calculateSubtotal();
                      return remaining > 0 ? (
                        <p className="text-xs text-accent">
                          Add KES {remaining.toLocaleString()} more for free shipping to {shippingInfo.city}!
                        </p>
                      ) : null;
                    })()}
                  </div>

                  <Separator />

                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total</span>
                    <span className="text-2xl font-bold font-mono">
                      KES {calculateTotal().toLocaleString()}
                    </span>
                  </div>

                  {/* Trust Badges */}
                  <div className="pt-4 border-t">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Shield className="w-4 h-4 text-green-600" />
                      Secure checkout with SSL encryption
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CheckoutPage;
